import { useEffect, useState, useMemo, useCallback } from "react";
import {
  useMutation,
  useQuery,
  useQueries,
  useIsMutating,
  useQueryClient,
  isCancelledError,
} from "@tanstack/react-query";
import { parseColor } from "react-stately";

import {
  convertImageBlobToType,
  createImageColorBlob,
  cropImageBlob,
  cropImageBlobToBbox,
  getImageBlobDimensions,
  resizeImageBlob,
  OBJECT_FIT,
  OBJECT_POSITION,
} from "@app/utils/imageBlob";
import {
  getExtensionFromFilePath,
  getQueryLoadingState,
  shouldAddMapUrl,
} from "@app/utils";
import { store } from "@app/store";
import { getImageBlob } from "@app/services/downloadFile";
import { searchAppStore } from "@app/services/appStore";
import { getAlloy } from "@app/utils/analytics";

import { trackVideoWizard } from "./usePageTracking";

import {
  useVideo,
  useCreateVideo,
  useUpdateVideo,
  useVideoTemplates,
  useVideoTemplate,
  videoKey,
} from "./videos";
import {
  expressTemplateKey,
  useExportRendition,
  useGenerateVariation,
  useExpressTemplate,
  // useTemplates,
} from "./expressTemplates";
import {
  useDescribeMogrtTemplate,
  useDescribeModularTemplate,
  useRenderMogrtTemplate,
  buildRenderPayload,
  modularTemplateQueryOptions,
} from "./mogrtTemplates";
import { renderModularTemplate as renderModularTemplateService } from "@app/services/mogrt";
import {
  assignScenesToModules,
  applyScenesToModuleControls,
  buildModulesFromScenes,
  collectTemplateVideoAssets,
  getVideoScenes,
} from "@app/utils/modularScenes";
import {
  useDrNumbers,
  useAccounts,
  // useDemoPilotProjects,
  // useDemoPilotProject,
  // useCreateDemoPilotProject,
  scrapeQueryOptions,
  searchImagesQueryOptions,
} from "./zoltarDemoPilot";
import { useZoltarMutation, getZoltarResponse } from "./zoltar";
import { parseZoltarGenerateAssistantJson } from "@app/services/zoltar";
import { useUserProfile, getUserLdap } from "./useIMS";
import {
  useUploadFile,
  usePresignURL,
  s3Key,
  presignURLQueryOptions,
} from "./s3";
import { useCropImage } from "./photoshop";
import { useDebounce } from "./useDebounce";

import { getCustomerBrandingPromptTemplate } from "@app/utils/systemPrompts";
import { useIsAdmin } from "@app/hooks/useRoles";
import { putAuroraVideo } from "@app/services/aurora";
import {
  WIZARD_STEP,
  SELECTION_TYPE,
  isStepAtOrAfter,
  isStepAfter,
  isStepAtOrBefore,
  hasAudioStep,
  hasImageVariationsStep,
  hasBannersStep,
  getStepName,
  resolveSteps,
  getStepEnterAction,
  getResumeImagesStepId,
  isImagesStep,
} from "@app/views/projects/wizardSteps";

export { hasImageVariationsStep, hasBannersStep, SELECTION_TYPE };

const EXPRESS_TAG_BATCH_SIZE = 50;

export const GENERATE_STATUS_TEXT = [
  "Integrating brand elements",
  "Embedding banners",
  "Placing text elements",
  "Applying smooth transitions",
  "Adjusting animations",
  // "Preparing editable Express file",
  "Cropping & balancing images",
];

export { getCustomerBrandingPromptTemplate };

export const parseCustomerBrandingResponse =
  (campaignSelectionType) => (data) => {
    if (campaignSelectionType === SELECTION_TYPE.SINGLE) {
      data.campaigns = (data?.campaigns ?? []).map((campaign) => ({
        ...campaign,
        id: crypto.randomUUID(),
      }));

      return data;
    } else if (campaignSelectionType === SELECTION_TYPE.NONE) {
      const ret = {
        campaigns: [{ id: crypto.randomUUID(), ...data }],
      };

      if (data?.["Brand-color"]) {
        ret.customer = {
          ...(ret.customer ?? {}),
          color: parseColor(data["Brand-color"]).toString("hex"),
        };
      }

      if (data?.["Secondary-color"]) {
        ret.customer = {
          ...(ret.customer ?? {}),
          secondaryColor: parseColor(data["Secondary-color"]).toString("hex"),
        };
      }

      if (data?.["Company-website-URL"]) {
        ret.customer = {
          ...(ret.customer ?? {}),
          websiteURL: data["Company-website-URL"],
        };
      }

      return ret;
    }

    return data;
  };

const useUpdateCustomer = (videoId) => {
  const updateVideo = useUpdateVideo(videoId);

  return useMutation({
    mutationFn: ({ customer }, { client }) => {
      const oldData = client.getQueryData(videoKey.detail(videoId));

      if (!!customer?.color?.alpha) {
        customer.color = customer.color.toString("hex");
      }

      return updateVideo.mutateAsync({
        customer: {
          ...(oldData?.customer ?? {}),
          ...customer,
        },
      });
    },
  });
};

const _getAllAssets = (videoId, client, assets = []) => {
  const oldData = client.getQueryData(videoKey.detail(videoId));
  const oldAssets = oldData?.assets ?? [];
  const byName = new Map();

  for (const asset of oldAssets) {
    if (!asset.isLoading) {
      byName.set(asset.name, asset);
    }
  }

  for (const incoming of assets) {
    const { src, presignURL, blob, skipClientPrepare, ...rest } = incoming;
    const existing = byName.get(rest.name);
    const merged = existing ? { ...existing, ...rest } : { ...rest };

    if (rest.parts != null) {
      merged.parts = { ...rest.parts };
    }

    byName.set(rest.name, merged);
  }

  return Array.from(byName.values());
};

const assetsWrites = new Map();

const queueAssetsWrite = (videoId, write) => {
  if (!videoId) {
    return Promise.resolve().then(write);
  }

  const previous = assetsWrites.get(videoId) ?? Promise.resolve();
  const next = previous.then(write, write);
  const settled = next.catch(() => {});

  assetsWrites.set(videoId, settled);

  settled.then(() => {
    if (assetsWrites.get(videoId) === settled) {
      assetsWrites.delete(videoId);
    }
  });

  return next;
};

export const useUpdateAssets = (videoId) => {
  const updateVideo = useUpdateVideo(videoId);

  return useMutation({
    mutationFn: ({ assets = [], toRemove = [] }, { client } = {}) =>
      queueAssetsWrite(videoId, () => {
        const _allAssets = _getAllAssets(videoId, client, assets);
        const shouldFilterByName = toRemove.some((asset) => !!asset.name);
        const shouldFilterByFilePath = toRemove.some(
          (asset) => !!asset.filePath,
        );
        const filteredAssets = _allAssets.filter(
          (asset) =>
            (!shouldFilterByName ||
              !toRemove.some((a) => !!a.name && a.name === asset.name)) &&
            (!shouldFilterByFilePath ||
              !toRemove.some(
                (a) => !!a.filePath && a.filePath === asset.filePath,
              )),
        );

        return updateVideo.mutateAsync({
          assets: filteredAssets,
        });
      }),
  });
};

export const useUploadAssets = (videoId, options = {}) => {
  const video = useVideo(videoId);
  const updateAssets = useUpdateAssets(videoId);
  const uploadFile = useUploadFile();
  const { onSuccess: userOnSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: async ({ assets = [], fileName }, { client } = {}) => {
      const skippedImages = [];
      const uploadPromises = assets.map(async (asset) => {
        const preparedAsset = { ...asset };

        if (
          !asset.filePath &&
          !asset.skipClientPrepare &&
          (asset.type === "image" || !asset.type)
        ) {
          try {
            let blob = asset.blob;
            if (!blob && asset.src) {
              ({ blob } = await getImageBlob({ url: asset.src }));
            }

            const isSvg = blob?.type === "image/svg+xml";
            const needsJpegConversion =
              !isSvg && !["image/jpeg", "image/png"].includes(blob?.type);
            if (isSvg) {
              blob = await convertImageBlobToType(blob, "png");
            }

            const dimensions = await getImageBlobDimensions(blob);
            const resizedBlob = await resizeImageBlob(blob, dimensions);
            preparedAsset.blob = needsJpegConversion
              ? await convertImageBlobToType(resizedBlob, "jpeg")
              : resizedBlob;
            preparedAsset.size =
              preparedAsset.blob === blob
                ? dimensions
                : await getImageBlobDimensions(preparedAsset.blob);
          } catch (error) {
            console.warn(
              `Skipping image "${asset.name}": preparation failed`,
              error,
            );
            skippedImages.push(asset.name);
            return null;
          }
        }

        if (preparedAsset.filePath) {
          if (preparedAsset.presignURL && client && s3Key?.presignURL) {
            client.setQueryData(s3Key.presignURL(preparedAsset.filePath), {
              filePath: preparedAsset.filePath,
              presignURL: preparedAsset.presignURL,
            });
          }
          const {
            src,
            blob,
            presignURL,
            skipClientPrepare,
            ...uploadedAsset
          } = preparedAsset;
          return uploadedAsset;
        }

        let fileExtension;
        if (preparedAsset.blob) {
          fileExtension = preparedAsset.blob.type.split("/")[1];
        } else {
          const defaultExtension =
            asset.type === "video"
              ? "mp4"
              : asset.type === "audio"
                ? "wav"
                : "jpg";
          fileExtension = getExtensionFromFilePath(asset.src, defaultExtension);
        }

        const dotIndex = asset.name.lastIndexOf(".");
        const baseName =
          dotIndex === -1 ? asset.name : asset.name.slice(0, dotIndex);

        const { filePath } = await uploadFile.mutateAsync({
          filePath: `${video.data.projectPath}/${baseName}.${fileExtension}`,
          fileURL: preparedAsset.blob ? undefined : asset.src,
          fileBlob: preparedAsset.blob,
        });
        const {
          src,
          blob,
          presignURL,
          skipClientPrepare,
          ...uploadedAsset
        } = preparedAsset;
        return { ...uploadedAsset, filePath };
      });

      const uploadedAssets = (await Promise.all(uploadPromises)).filter(
        (asset) => asset !== null,
      );

      if (assets.length > 0 && uploadedAssets.length === 0) {
        throw new Error(
          `No assets could be prepared: ${skippedImages.join(", ")}`,
        );
      }

      if (skippedImages.length > 0) {
        store
          .getState()
          .addToast(
            `Skipped images that could not be prepared: ${skippedImages.join(", ")}`,
            { variant: "negative", timeout: 1000 * 60 },
          );
      }

      const allAssets = [...uploadedAssets];

      // Reference the uploaded asset with a specific fileName
      if (
        typeof fileName === "string" &&
        assets.length === 1 &&
        uploadedAssets.length === 1
      ) {
        const uploadedAsset = uploadedAssets[0];

        allAssets.push({
          ...uploadedAsset,
          name: fileName,
          referenceKey: uploadedAsset.name,
        });
      }

      return updateAssets.mutateAsync({
        assets: allAssets,
      });
    },
    ...restOptions,
    onSuccess: (data, variables, onMutateResult, context) => {
      const { client } = context;

      if (data) {
        client.setQueryData(videoKey.detail(videoId), data);
      }

      userOnSuccess?.(data, variables, onMutateResult, context);
    },
    onMutate: (variables, context) => {
      const { assets = [], fileName } = variables;
      const { client } = context;
      const oldData = client.getQueryData(videoKey.detail(videoId));
      const newAssets = [...assets];

      if (typeof fileName === "string" && newAssets.length === 1) {
        newAssets.push({
          ...newAssets[0],
          name: fileName,
        });
      }

      client.setQueryData(videoKey.detail(videoId), {
        ...oldData,
        assets: _getAllAssets(
          videoId,
          client,
          newAssets.map((asset) => ({
            ...asset,
            ...(!!asset?.blob ? { src: URL.createObjectURL(asset.blob) } : {}),
            isDisabled: true,
            isLoading: true,
          })),
        ),
      });

      restOptions?.onMutate?.(variables, context);

      return { oldData };
    },
    onError: (err, variables, onMutateResult, context) => {
      const { oldData } = onMutateResult;
      const { client } = context;
      client.setQueryData(videoKey.detail(videoId), oldData);

      restOptions?.onError?.(err, variables, onMutateResult, context);
    },
    meta: {
      errorMessage: "Failed to upload assets. Please try again.",
    },
  });
};

export const useCustomerCheck = (options = {}) => {
  return useMutation({
    mutationFn: async ({ customerName, hint }, { signal }) => {
      const response = await getZoltarResponse(
        {
          promptTemplate: [
            {
              role: "user",
              prompt_template: `[Company]: ${customerName}${hint ? `\n[Hints]: ${hint}` : ""}`,
            },
          ],
          systemPromptId: "common-customer-check",
          skills: ["vertex-ai"],
        },
        { signal },
      );

      const data = parseZoltarGenerateAssistantJson(
        response,
        "Failed to validate customer",
      );

      if (!data?.exists) {
        throw new Error(
          `We couldn’t find this company (${data?.["Company-name"]}) online. Please check the name and try again.`,
        );
      }

      return data;
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      if (options?.onSuccess) {
        await Promise.resolve(
          options.onSuccess(data, variables, onMutateResult, context),
        );
      }
    },
    ...options,
  });
};

const useCustomerBranding = (
  { customerName, hint, product, personaType, include3DDigitalTwin, videoId },
  options = {},
) => {
  const isAdmin = useIsAdmin();
  const video = useVideo(videoId);
  const template = useVideoTemplate(video.data?.templateId);
  const updateVideo = useUpdateVideo(videoId);
  const updateAssets = useUpdateAssets(videoId);

  const isVideoUpdating =
    useIsMutating({ mutationKey: videoKey.detail(videoId) }) > 0;

  const templateId = video.data?.templateId;

  const { enabled: enabledOption, ...mutationOptions } = options;

  const isEnabled =
    (enabledOption ?? true) &&
    !!customerName &&
    !!video.isSuccess &&
    !video.data?.campaigns?.length;

  const branding = useZoltarMutation(
    {
      ...mutationOptions,
      onSuccess: async (data, variables, onMutateResult, context) => {
        if (mutationOptions?.onSuccess) {
          await Promise.resolve(
            mutationOptions.onSuccess(data, variables, onMutateResult, context),
          );
        }

        const videoData = context.client.getQueryData(videoKey.detail(videoId));

        const existingData = {
          customer: videoData?.customer ?? {},
          campaigns: videoData?.campaigns ?? [],
        };
        const fetchData = data?.campaigns
          ? data
          : {
              customer: {},
              campaigns: [data],
            };

        const update = {};

        if (videoData?.templateId === "firefly-enterprise") {
          const selectedInclude3DDigitalTwin =
            videoData?.include3DDigitalTwin ??
            videoData?.campaigns?.find(
              (campaign) => campaign.id === videoData?.selectedCampaignIds?.[0],
            )?.["Include-3D-Digital-Twin"] ??
            include3DDigitalTwin;

          fetchData.campaigns = fetchData.campaigns.map((campaign) => ({
            ...campaign,
            "Include-3D-Digital-Twin": selectedInclude3DDigitalTwin,
          }));
        }

        const combinedCampaigns = Array.from(
          new Map(
            [...existingData.campaigns, ...(fetchData?.campaigns ?? [])].map(
              (campaign) => [campaign.id, campaign],
            ),
          ).values(),
        ).slice(-10);

        if (combinedCampaigns.length > existingData.campaigns.length) {
          update.campaigns = combinedCampaigns;

          if (template.data?.campaignSelectionType === SELECTION_TYPE.NONE) {
            update.selectedCampaignIds = [
              combinedCampaigns[combinedCampaigns.length - 1].id,
            ];
          }
        }

        if (
          Object.keys(fetchData?.customer ?? {}).length >
          Object.keys(existingData.customer ?? {}).length
        ) {
          update.customer = {
            ...fetchData.customer,
            name: customerName,
          };
        }

        const assetsUpdate = new Map();

        if (
          fetchData.customer?.color &&
          !videoData?.assets?.some((asset) => asset.name === "Brand-color")
        ) {
          assetsUpdate.set("Brand-color", {
            name: "Brand-color",
            type: "color",
            color: fetchData.customer?.color,
          });
        }

        if (
          fetchData.customer?.secondaryColor &&
          !videoData?.assets?.some((asset) => asset.name === "Secondary-color")
        ) {
          assetsUpdate.set("Secondary-color", {
            name: "Secondary-color",
            type: "color",
            color: fetchData.customer?.secondaryColor,
          });
        }

        if (
          template.data?.assets.some(
            (templateAsset) =>
              templateAsset.type === "color" &&
              !assetsUpdate.has(templateAsset.name) &&
              !videoData?.assets?.some(
                (asset) => asset.name === templateAsset.name,
              ),
          )
        ) {
          const newColorAssets = template.data.assets
            .filter(
              (templateAsset) =>
                templateAsset.type === "color" &&
                !assetsUpdate.has(templateAsset.name) &&
                !videoData?.assets?.some(
                  (asset) => asset.name === templateAsset.name,
                ),
            )
            .map((asset) => ({
              name: asset.name,
              type: asset.type,
              color:
                fetchData.campaigns?.[0]?.[asset.name] ??
                videoData?.customer?.[asset.name] ??
                "",
            }))
            .filter((asset) => !!asset.color);

          if (newColorAssets.length) {
            newColorAssets.forEach((asset) => {
              assetsUpdate.set(asset.name, asset);
            });
          }
        }

        const updatePromises = [];

        if (Object.keys(update).length) {
          updatePromises.push(updateVideo.mutateAsync(update));
        }

        if (assetsUpdate.size) {
          updatePromises.push(
            updateAssets.mutateAsync({
              assets: Array.from(assetsUpdate.values()),
            }),
          );
        }

        return await Promise.all(updatePromises);
      },
    },
    (response, context) => {
      const { client } = context;
      const videoData = client.getQueryData(videoKey.detail(videoId));

      let campaignSelectionType =
        template.data?.campaignSelectionType ?? SELECTION_TYPE.NONE;

      // FIXME: template.data is still fetching, we need to pass `campaignSelectionType`
      // as a parameter to the `refetch` function instead of relying on `template.data`.
      if (videoData?.templateId === "csc-overview") {
        campaignSelectionType = SELECTION_TYPE.SINGLE;
      }

      return parseCustomerBrandingResponse(campaignSelectionType)(
        parseZoltarGenerateAssistantJson(
          response,
          "Failed to generate content",
        ),
      );
    },
  );

  // // Trigger branding generation once the conditions are met.
  // // Replaces the auto-running `enabled` option of the previous infiniteQuery.
  // useEffect(() => {
  //   if (
  //     isEnabled &&
  //     branding.isIdle &&
  //     (template.isSuccess || !template.isEnabled)
  //   ) {
  //     branding.mutate(mutateOptions);
  //   }
  // }, [isEnabled, branding.isIdle, template.isSuccess, template.isEnabled]);

  // Preserve the query-like surface consumed by the wizard views:
  // `isEnabled`/`isPending` gate the loading UI and `refetch` retries on error.
  branding.isEnabled = isEnabled;
  // branding.isPending =
  //   branding.isPending ||
  //   (isEnabled && !branding.isSuccess && !branding.isError);

  branding.getPromptTemplate = (videoData) =>
    getCustomerBrandingPromptTemplate({
      customerName: videoData?.customer?.name ?? customerName,
      hint: videoData?.hint ?? hint,
      editScope: videoData?.editScope,
      product: videoData?.product ?? product,
      personaType: videoData?.personaType ?? personaType,
      templateId: videoData?.templateId ?? templateId,
    });

  const getRefetchVariables = (videoData, getPromptTemplate) => {
    const selectedCampaign =
      template.data?.campaignSelectionType === SELECTION_TYPE.NONE
        ? videoData?.campaigns?.find(
            (campaign) => campaign.id === videoData?.selectedCampaignIds?.[0],
          )
        : videoData?.campaigns;

    const promptParams = {
      customerName: videoData?.customer?.name ?? customerName,
      hint: videoData?.hint ?? hint,
      editScope: videoData?.editScope,
      product: videoData?.product ?? product,
      personaType: videoData?.personaType ?? personaType,
      templateId: videoData?.templateId ?? templateId,
    };
    const promptTemplate = getCustomerBrandingPromptTemplate(promptParams);

    if (selectedCampaign) {
      promptTemplate.template = promptTemplate.template.toSpliced(0, 0, {
        role: "user",
        prompt_template: `[Current data]:
${JSON.stringify(selectedCampaign)}`,
      });
    }

    return {
      model:
        videoData?.templateId === "csc-overview"
          ? "gpt-5.6-luna"
          : "gpt-5.6-sol",
      promptTemplate: getPromptTemplate
        ? getPromptTemplate(videoData, promptTemplate.template)
        : promptTemplate.template,
      systemPromptId: promptTemplate.systemPromptId,
      promptOverrideKey: isAdmin
        ? `customTemplate__${promptParams.templateId}`
        : undefined,
      threadId: selectedCampaign?.thread_id,
      // FIXME: 400 An assistant message with 'tool_calls' must be followed by tool messages responding to each 'tool_call_id'
      skills:
        videoData?.templateId === "csc-overview" ? undefined : ["vertex-ai"],
    };
  };

  branding.refetch = (videoData, getPromptTemplate, options = {}) => {
    branding.mutate(getRefetchVariables(videoData, getPromptTemplate), options);
  };

  // Same as `refetch`, but resolves once the generated campaign has been
  // persisted — and rejects on failure — so a caller can await it.
  branding.refetchAsync = (videoData, getPromptTemplate, options = {}) =>
    branding.mutateAsync(
      getRefetchVariables(videoData, getPromptTemplate),
      options,
    );

  return branding;
};

const useAddCampaign = (videoId, options = {}) => {
  const updateVideo = useUpdateVideo(videoId);

  return useMutation({
    mutationFn: (variables, { client }) => {
      const oldData = client.getQueryData(videoKey.detail(videoId));

      updateVideo.mutate({
        campaigns: [
          ...(oldData.campaigns ?? []),
          { ...variables, id: crypto.randomUUID() },
        ],
      });
    },
    ...options,
  });
};

export const useEditCampaign = (videoId, options = {}) => {
  const updateVideo = useUpdateVideo(videoId);

  return useMutation({
    mutationFn: (variables, { client }) => {
      const oldData = client.getQueryData(videoKey.detail(videoId));

      return updateVideo.mutateAsync({
        campaigns: oldData.campaigns.map((item) =>
          item.id === variables.id ? { ...item, ...variables } : item,
        ),
      });
    },
    ...options,
  });
};

export const useExportBanners = (videoId, options = {}) => {
  const exportRendition = useExportRendition();
  return useQuery({
    queryKey: expressTemplateKey.export(videoId, "image/jpeg"),
    queryFn: async (context) => {
      const { client } = context;
      const { templateAssetsRenditions = [] } =
        client.getQueryData(videoKey.detail(videoId)) ?? {};

      const data = await Promise.all(
        templateAssetsRenditions.map(({ documentId }) =>
          exportRendition.mutateAsync({
            id: documentId,
            pages: "1-",
            options: {
              format: "image/jpeg",
              // size: 1920,
            },
          }),
        ),
      );

      const ret = data.flatMap((rendition) =>
        rendition.pageRenditionsResult.map((page) => ({
          id: rendition.id,
          ...page,
        })),
      );

      if (ret.some((item) => item.error)) {
        console.warn("Some renditions failed to export:", data);
      }

      return ret;
    },
    enabled: !!videoId && (options?.enabled ?? true),
    ...options,
  });
};

export const useExportExpressVideo = (videoId, options = {}) => {
  const video = useVideo(videoId);
  const updateVideo = useUpdateVideo(videoId);
  const exportRendition = useExportRendition();
  const uploadFile = useUploadFile();
  const videoFile = usePresignURL(video.data?.videoPath);

  return useMutation({
    // queryKey: expressTemplateKey.export(video.data?.expressId, "video/mp4"),
    mutationFn: async (_, options = {}) => {
      if (video.data?.videoPath) {
        while (videoFile.isLoading) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        return videoFile.data;
      }

      let exportData =
        exportRendition.data?.pageRenditionsResult?.[0]?.renditionUrl;

      if (!exportData) {
        exportData = await exportRendition.mutateAsync(
          {
            id: video.data?.expressId,
            pages: "1-",
            options: {
              format: "video/mp4",
              // size: 1080,
            },
          },
          options,
        );
      }

      const ret = await uploadFile.mutateAsync({
        fileURL: exportData.pageRenditionsResult?.[0]?.renditionUrl,
        filePath: `${video.data.projectPath}/${video.data.name}.mp4`,
      });

      updateVideo.mutate({
        videoPath: ret.filePath,
      });

      return ret;
    },
    ...options,
    enabled:
      !!videoId &&
      video.isSuccess &&
      !!video.data?.expressId &&
      (options?.enabled ?? true),
  });
};

export const useVideoPersonas = ({ videoId, templateId }, options = {}) => {
  const video = useVideo(videoId);
  const updateVideo = useUpdateVideo(videoId);
  const template = useVideoTemplate(video.data?.templateId ?? templateId);

  const personaAvatars = usePresignURL(
    template.data?.personas?.map((persona) => persona.avatarPath),
    {
      enabled: !!template.data?.personas?.length,
    },
  );

  const personas =
    template.data?.personas?.map((persona, idx) => ({
      ...persona,
      avatarUrl: personaAvatars.data?.urls?.[idx]?.presignURL,
    })) ?? [];

  const [_selectedPersonaKey, setSelectedPersonaKey] = useState();
  const selectedPersonaKey =
    _selectedPersonaKey ??
    (video.isEnabled ? video.data?.personaId : undefined) ??
    personas?.[0]?.id;

  const selectedPersona = personas?.find(
    (persona) => persona.id === selectedPersonaKey,
  );

  const setSelectedPersona = (personaKey) => {
    setSelectedPersonaKey(personaKey);

    if (video.isEnabled) {
      const update = {
        personaId: personaKey,
      };

      if (!!video.data?.personaId && personaKey !== video.data?.personaId) {
        // Trigger campaign regeneration
        // TODO: should we trigger confirmation modal if persona is changed after campaign focus step?
        update.customer = {
          name: video.data?.customer?.name,
        };
        // update.assets = [];
        update.campaigns = [];
        update.selectedCampaignIds = [];
      }

      updateVideo.mutate(update);
    }
  };

  return {
    personas,
    selectedPersona,
    setSelectedPersona,
  };
};

export const useVideoVerticals = ({ videoId, templateId }, options = {}) => {
  const video = useVideo(videoId);
  const updateVideo = useUpdateVideo(videoId);
  const updateAssets = useUpdateAssets(videoId);
  const template = useVideoTemplate(video.data?.templateId ?? templateId);

  const verticals = template.data?.verticals ?? [];

  const [_selectedKey, setSelectedKey] = useState();
  const selectedKey =
    _selectedKey ??
    (video.isEnabled ? video.data?.verticalKey : undefined) ??
    template.data?.verticals?.find((vertical) => vertical.isDefault)?.key ??
    "generic";

  const selectedVertical = verticals?.find(
    (vertical) => vertical.key === selectedKey,
  );

  const setSelectedVertical = (verticalKey) => {
    setSelectedKey(verticalKey);

    if (
      video.isEnabled &&
      !!video.data?.verticalKey &&
      verticalKey !== video.data?.verticalKey
    ) {
      updateVideo.mutate(
        {
          verticalKey,
        },
        {
          onSuccess: (data) => {
            const templateVerticalAssets = verticals?.find(
              (vertical) => vertical.key === data.verticalKey,
            )?.assets;

            const verticalAssets = templateVerticalAssets?.length
              ? { assets: templateVerticalAssets }
              : {
                  toRemove: [
                    { name: "Footage-Industry--A" },
                    { name: "Footage-Industry--B" },
                  ],
                };

            updateAssets.mutate(verticalAssets);
          },
        },
      );
    }
  };

  return {
    verticals,
    selectedVertical,
    setSelectedVertical,
  };
};

export const resolveMogrtRootPath = (templateData, selectedCampaign = {}) => {
  const variant = (templateData?.mogrtVariants ?? []).find((entry) => {
    if (!entry?.rootPath) {
      return false;
    }
    if (!entry?.condition) {
      return true;
    }
    try {
      return !!new Function("selectedCampaign", `return (${entry.condition});`)(
        selectedCampaign,
      );
    } catch (error) {
      console.warn("Failed to evaluate mogrt condition:", entry, error);
      return false;
    }
  });

  return variant?.rootPath ?? templateData?.rootPath;
};

export const useVideoWizard = ({
  videoId,
  routeStep,
  customerName,
  hint,
  product,
  include3DDigitalTwin: initialInclude3DDigitalTwin,
  templateType,
  templateId,
  sfType,
  drNumbersFilter,
  drNumber,
  accountName,
  demoPilotProjectId,
  onCreateProject,
  onSuccess,
}) => {
  const userProfile = useUserProfile();

  const [isVideoCreated, setIsVideoCreated] = useState(!!videoId);

  const createVideo = useCreateVideo();
  const video = useVideo(videoId, {
    enabled: !!videoId && isVideoCreated,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.templateType === "modular" &&
        data?.modularJobs?.length &&
        !data?.videoPath
        ? 10000
        : false;
    },
  });

  const [_currentStep, _setCurrentStep] = useState();
  // Only honour a persisted step if it's a string id (the new format). Legacy
  // numeric values are ignored so the bootstrap below recomputes a valid step.
  const persistedStep =
    typeof video.data?.currentStep === "string" ? video.data.currentStep : null;
  // When the URL carries a step (`/projects/new/:id/:step`), it drives the initial
  // step (validated in the bootstrap effect) and takes priority over the
  // persisted step; otherwise fall back to the persisted step.
  const currentStep =
    _currentStep ?? (routeStep ? null : persistedStep) ?? null;

  const setCurrentStep = (step) => {
    _setCurrentStep(step);

    if (video.isEnabled && video.data?.currentStep !== step) {
      updateVideo.mutate({
        currentStep: step,
      });
    }
  };

  const queryClient = useQueryClient();

  useEffect(() => {
    // Clear query cache
    if (!videoId && Object.keys(video.data || {}).length > 0) {
      queryClient.setQueryData(videoKey.detail(videoId), null);
    }
  }, [videoId, video.data]);

  const updateVideo = useUpdateVideo(videoId);
  const updateCustomer = useUpdateCustomer(videoId);
  const updateAssets = useUpdateAssets(videoId);
  const uploadAssets = useUploadAssets(videoId);
  const uploadFile = useUploadFile();

  const [_include3DDigitalTwin, _setInclude3DDigitalTwin] = useState();
  const include3DDigitalTwin =
    _include3DDigitalTwin ??
    (video.isEnabled ? video.data?.include3DDigitalTwin : undefined) ??
    video.data?.campaigns?.find(
      (campaign) => campaign.id === video.data?.selectedCampaignIds?.[0],
    )?.["Include-3D-Digital-Twin"] ??
    video.data?.campaigns?.[0]?.["Include-3D-Digital-Twin"] ??
    initialInclude3DDigitalTwin ??
    true;

  const setInclude3DDigitalTwin = (value) => {
    const previousValue = _include3DDigitalTwin;
    _setInclude3DDigitalTwin(value);

    if (video.isEnabled && video.data?.id) {
      updateVideo.mutate(
        {
          include3DDigitalTwin: value,
          ...(video.data?.campaigns?.length
            ? {
                campaigns: video.data.campaigns.map((campaign) => ({
                  ...campaign,
                  "Include-3D-Digital-Twin": value,
                })),
              }
            : {}),
        },
        { onError: () => _setInclude3DDigitalTwin(previousValue) },
      );
    }
  };

  const templates = useVideoTemplates({ type: templateType });
  const template = useVideoTemplate(templateId ?? video.data?.templateId);

  // The current step's configuration — its `type`, not its id, tells e.g. an
  // images stage step apart, which the step-order gates below depend on.
  const currentStepEntry = useMemo(
    () =>
      resolveSteps(templateId ?? template.data?.id, template.data).find(
        (step) => step.id === currentStep,
      ) ?? currentStep,
    [templateId, template.data, currentStep],
  );

  // const hasAudioStep = template.data?.assets?.some(
  //   (asset) => asset.type === "audio",
  // );

  const { personas, selectedPersona, setSelectedPersona } = useVideoPersonas({
    videoId,
    templateId,
  });

  const personaId = selectedPersona?.id;
  const personaType = selectedPersona?.type;

  const { verticals, selectedVertical, setSelectedVertical } =
    useVideoVerticals({ videoId, templateId });

  const verticalKey = selectedVertical?.key;

  const expressTemplate = useExpressTemplate(
    video.data?.expressTemplateId ?? template.data?.expressId,
    {
      enabled:
        !!(video.data?.expressTemplateId ?? template.data?.expressId) &&
        template.data?.type === "express",
    },
  );

  const assetsTemplate = useExpressTemplate(
    video.data?.assetsTemplateId ?? template.data?.assetsTemplateId,
  );

  // Migrate old templateId field to the new expressTemplateId
  useEffect(() => {
    // TODO: remove after full migration
    //
    if (
      video.isEnabled &&
      video.isSuccess &&
      template.isSuccess &&
      !updateVideo.isPending &&
      video.data?.templateId?.startsWith("urn:")
    ) {
      updateVideo.mutate({
        templateId: template.data?.id,
        templateType: template.data?.type,
        expressTemplateId: video.data?.templateId,
      });
    }
  }, [
    video.isEnabled,
    video.isSuccess,
    video.data?.templateId,
    template.isSuccess,
    template.data?.id,
    template.data?.type,
    updateVideo.isPending,
  ]);

  // Migrate old image variations structure to the new one
  useEffect(() => {
    // TODO: remove after full migration
    if (video.isEnabled && video.isSuccess && !updateVideo.isPending) {
      if (
        !video.data?.imageVariations ||
        !Array.isArray(video.data.imageVariations)
      ) {
        const imageVariations = [];

        Object.entries(video.data?.abstractVariations || {}).forEach(
          ([key, arr]) => {
            arr.forEach((variation) => {
              imageVariations.push({
                referenceKey: key,
                type: "abstract",
                ...variation,
              });
            });
          },
        );

        Object.entries(video.data?.audiencesVariations || {}).forEach(
          ([key, arr]) => {
            if (!Array.isArray(arr)) {
              return;
            }

            arr.forEach((variation) => {
              imageVariations.push({
                referenceKey: key,
                type: "audience",
                ...variation,
              });
            });
          },
        );

        updateVideo.mutate({
          imageVariations,
          abstractVariations: null,
          audiencesVariations: null,
        });
      }
    }
  }, [video.isEnabled, video.isSuccess, video.data, updateVideo.isPending]);

  const drNumbers = useDrNumbers(drNumbersFilter, {
    enabled:
      sfType === "drNumber" && !!drNumbersFilter && drNumbersFilter.length >= 3,
  });

  const accounts = useAccounts(accountName, {
    enabled:
      sfType === "accountName" && !!accountName && accountName.length >= 3,
  });

  // Generate customer branding info
  const customerBranding = useCustomerBranding({
    customerName: video.data?.customer?.name ?? customerName,
    hint,
    product,
    personaType,
    include3DDigitalTwin,
    videoId,
  });

  const addCampaign = useAddCampaign(videoId);
  const editCampaign = useEditCampaign(videoId);

  // const demoPilotProjects = useDemoPilotProjects();
  // const demoPilotProject = useDemoPilotProject(demoPilotProjectId);
  // const createDemoPilotProject = useCreateDemoPilotProject();

  const selectedCampaign = useMemo(() => {
    return video.data?.campaigns?.find(
      (c) => c.id === video.data?.selectedCampaignIds?.[0],
    );
  }, [video.data?.campaigns, video.data?.selectedCampaignIds]);

  // Set `data.urls[]`
  useEffect(() => {
    if (!video.data?.id) {
      return;
    }

    const urlsMap = new Map();

    video.data?.selectedCampaignIds
      ?.map((id) =>
        video.data?.campaigns?.find((campaign) => campaign.id === id),
      )
      ?.filter(Boolean)
      ?.forEach((item) => {
        // if (item.landingPageURL) {
        //   urlsMap.set(item.landingPageURL, `Campaign: ${item.title}`);
        // }

        if (shouldAddMapUrl(urlsMap, item["Campaign-product-URL"])) {
          urlsMap.set(
            item["Campaign-product-URL"],
            `Primary offer: ${item["Campaign-product"]}`,
          );
        }

        if (shouldAddMapUrl(urlsMap, item["NBA-campaign-product-URL"])) {
          urlsMap.set(
            item["NBA-campaign-product-URL"],
            `Next best offer: ${item["NBA-campaign-product"]}`,
          );
        }

        if (shouldAddMapUrl(urlsMap, item["Persona-2-campaign-product-URL"])) {
          urlsMap.set(
            item["Persona-2-campaign-product-URL"],
            `Secondary persona offer: ${item["Persona-2-campaign-product"]}`,
          );
        }

        Object.entries(item)
          .filter(([, value]) => value?.startsWith?.("http"))
          .forEach(([key, value]) => {
            if (shouldAddMapUrl(urlsMap, value)) {
              const prefixKey = key.replace(/-URL$/i, "").trim();
              urlsMap.set(
                value,
                item[prefixKey] ?? prefixKey.replace("-", " "),
              );
            }
          });
      });

    if (shouldAddMapUrl(urlsMap, video.data?.customer?.websiteURL)) {
      urlsMap.set(video.data?.customer?.websiteURL, "Customer Website");
    }

    if (shouldAddMapUrl(urlsMap, video.data?.customer?.brandCenterURL)) {
      urlsMap.set(video.data?.customer?.brandCenterURL, "Brand Guidelines");
    }

    video.data?.urls?.forEach((_url) => {
      const [url, label = ""] = Array.isArray(_url) ? _url : [_url];
      if (shouldAddMapUrl(urlsMap, url)) {
        urlsMap.set(url, label);
      }
    });

    const urls = Array.from(urlsMap);

    if (
      !updateVideo.isPending &&
      urls.length > 0 &&
      urls.length !== (video.data?.urls || []).length
    ) {
      updateVideo.mutate({
        urls,
      });
    }
  }, [
    updateVideo.isPending,
    video.data?.urls,
    video.data?.selectedCampaignIds,
    video.data?.campaigns,
    video.data?.customer?.websiteURL,
    video.data?.customer?.brandCenterURL,
  ]);

  const _mapAssets = useCallback((data) => {
    const images =
      data.images
        .map((asset) => {
          // if (asset.type === "image") {
          if (
            !asset?.src?.startsWith?.("data:image/svg+xml") &&
            asset?.width &&
            asset?.height
          ) {
            const area = asset.width * asset.height;

            return {
              ...asset,
              isDisabled: area < 320 * 240,
            };
          }

          return {
            origin: asset.origin,
            ...(asset?.data ?? {}),
            type: "image",
            isDisabled: false,
          };
          // }

          return asset;
        })
        .sort((a, b) => (b.isDisabled ? -1 : 1)) || [];

    const colorValues = new Set(
      [
        ...data.brandColors.map((color) => color?.data?.value),
        ...[
          "primaryButton",
          "secondaryButton",
          "body",
          "header",
          "footer",
        ].flatMap((key) =>
          ["background", "text"].map(
            (subKey) => data?.colors?.[key]?.[subKey]?.data?.value,
          ),
        ),
      ].filter(Boolean),
    );
    const colors = Array.from(colorValues).map((color) => ({
      type: "color",
      color,
    }));

    const assets = [...images, ...colors];

    assets.isFiltered = images.some((image) => image?.isDisabled === true);

    return assets;
  }, []);

  const [...scrapeQueries] = useQueries({
    queries:
      video.data?.urls?.map(([url]) =>
        scrapeQueryOptions(
          url,
          {},
          {
            enabled:
              !!url &&
              isStepAfter(currentStepEntry, WIZARD_STEP.CAMPAIGN) &&
              isStepAtOrBefore(currentStepEntry, WIZARD_STEP.BRANDING),
          },
          { shouldParseAssets: true, dataParser: _mapAssets },
        ),
      ) ?? [],
  });

  scrapeQueries.forEach((_query, idx) => {
    const [url, label] = video.data?.urls?.[idx] ?? [];
    _query.label = label;
    _query.url = url;
    _query.isEditable = true;
  });

  const searchURLs = useMemo(() => {
    try {
      const videoUrls = new Map(video.data?.urls ?? []);
      const urls = new Map();
      const websiteUrl = new URL(video.data?.customer?.websiteURL);
      const baseDomain = websiteUrl.host.replace(/^www\./, "");
      const baseQuery = `site:${baseDomain}`;
      const selectedCampaign =
        video.data?.campaigns?.find(
          (c) => c.id === video.data?.selectedCampaignIds?.[0],
        ) ?? {};

      const companyWebsiteKey = websiteUrl.host + websiteUrl.pathname;

      Object.entries(selectedCampaign)
        .filter(([, value]) => value?.startsWith?.("http"))
        .forEach(([key, val]) => {
          try {
            const url = new URL(val);
            const value = url.host + url.pathname;

            if (!urls.has(value) && value !== companyWebsiteKey) {
              const prefixKey = key.replace(/-URL$/i, "").trim();

              const name = selectedCampaign[prefixKey];
              const label = videoUrls.has(val)
                ? videoUrls.get(val)
                : (name ?? prefixKey.replace("-", " "));
              urls.set(value, {
                label: `[Search] ${label}`,
                url: val,
                query: `site:${url.host.replace(/^www\./, "")} ${name ?? label}`,
                isEditable: true,
              });
            }
          } catch (e) {
            console.warn(`Invalid URL skipped: ${val}`);
          }
        });

      // if (!urls.has(websiteUrl.host + websiteUrl.pathname)) {
      urls.set(companyWebsiteKey, {
        label: "[Search] Company website",
        url: websiteUrl.host,
        query: baseQuery,
      });

      urls.set("logotype", {
        label: "[Search] Company logo",
        url: websiteUrl.host,
        query: `type:transparent ${video.data?.customer?.name ?? ""} Logo`,
      });
      // }

      videoUrls.entries().forEach(([url, label]) => {
        try {
          const parsedUrl = new URL(url);
          const value = parsedUrl.host + parsedUrl.pathname;

          if (!urls.has(value)) {
            urls.set(value, {
              label: `[Search] ${label || url}`,
              url,
              query: `site:${parsedUrl.host.replace(/^www\./, "")}${parsedUrl.pathname !== "/" ? ` ${parsedUrl.pathname}` : ""}`,
              isEditable: true,
            });
          }
        } catch (e) {
          console.warn(`Invalid URL skipped: ${url}`);
        }
      });

      return Array.from(urls);
    } catch (e) {
      console.warn(
        "Invalid customer website URL:",
        video.data?.customer?.websiteURL,
      );
      return [];
    }
  }, [
    video.data?.customer?.websiteURL,
    video.data?.campaigns,
    video.data?.selectedCampaignIds,
    video.data?.urls,
  ]);

  const _searchImagesQueries =
    searchURLs?.map(([, { query }]) =>
      searchImagesQueryOptions(
        { q: query, iterations: 1, moderate: true, retries: 1 },
        {
          enabled:
            !!query &&
            isStepAfter(currentStepEntry, WIZARD_STEP.CAMPAIGN) &&
            isStepAtOrBefore(currentStepEntry, WIZARD_STEP.BRANDING),
        },
      ),
    ) ?? [];

  const [...searchImagesQueries] = useQueries({
    queries: _searchImagesQueries,
  });

  searchImagesQueries.forEach((_query, idx) => {
    const { url, query, label, isEditable } = searchURLs?.[idx]?.[1] ?? {};
    _query.label = label;
    _query.query = `https://www.bing.com/images/search?q=${encodeURIComponent(query)}`;
    _query.url = _query.query;
    _query.isEditable = false;
  });

  const appStoreQueryParams = {
    term: video.data?.customer?.name,
    limit: 15,
    country: "US",
    media: "software",
  };

  const _appStoreQuery = useQuery({
    queryKey: ["appStore", "search", appStoreQueryParams],
    queryFn: ({ signal }) => searchAppStore(appStoreQueryParams, { signal }),
    enabled:
      !!video.data?.projectPath &&
      !!video.data?.customer?.name &&
      isStepAtOrBefore(currentStepEntry, WIZARD_STEP.BRANDING),
    select: useCallback(
      (data) =>
        data.results
          .filter((app) =>
            [app.artistName, app.sellerName, app.trackName]
              .map((str) => str.toLowerCase().replaceAll(/[^a-z0-9]/g, ""))
              .some((str) =>
                str.includes(
                  video.data?.customer?.name
                    .toLowerCase()
                    .replaceAll(/[^a-z0-9]/g, ""),
                ),
              ),
          )
          .flatMap((app, idx) => {
            if (idx === 0) {
              if (
                !!app.artworkUrl512 &&
                !video.data?.assets?.some((a) => a.name === "App-icon") &&
                !uploadAssets.isPending
              ) {
                uploadAssets.mutate({
                  assets: [
                    {
                      name: "App-icon",
                      type: "image",
                      src: app.artworkUrl512,
                      size: {
                        width: 512,
                        height: 512,
                      },
                    },
                  ],
                });
              }

              if (!!app.sellerUrl) {
                const urlsMap = new Map(video.data?.urls ?? []);
                const stripProtocol = (url) => url.replace(/^https?:\/\//, "");
                const hasSellerUrl = Array.from(urlsMap.keys()).some(
                  (url) => stripProtocol(url) === stripProtocol(app.sellerUrl),
                );

                if (!hasSellerUrl && !updateVideo.isPending) {
                  urlsMap.set(app.sellerUrl, "App Store: Website");

                  updateVideo.mutate({
                    urls: Array.from(urlsMap),
                  });
                }
              }
            }

            const images = [
              {
                id: `${app.trackId}-icon`,
                type: "image",
                src: app.artworkUrl512,
                alt: app.trackName,
                width: 512,
                height: 512,
                type: "image",
                name: "App Store Image",
                isDisabled: false,
              },
            ];

            if (app.screenshotUrls?.length) {
              images.push(
                ...app.screenshotUrls.map((url, index) => {
                  const [width, height] = /\/(\d+)x(\d+)/
                    .exec(url)
                    ?.slice(1, 3) || [1080, 1920];

                  return {
                    id: `${app.trackId}-screenshot-${index}`,
                    type: "image",
                    src: url,
                    alt: `${app.trackName} Screenshot ${index + 1}`,
                    width,
                    height,
                    type: "image",
                    name: `App Store Screenshot ${index + 1}`,
                    isDisabled: false,
                  };
                }),
              );
            }

            return images;
          }),
      [
        video.data?.customer?.name,
        video.data?.assets,
        video.data?.urls,
        uploadAssets.isPending,
        updateVideo.isPending,
      ],
    ),
  });

  const appStoreQuery = Object.assign(
    {
      label: "App Store",
      url: `https://apps.apple.com/iphone/search?term=${video.data?.customer?.name}`,
    },
    _appStoreQuery,
  );

  const _productSearchQuery = useQuery(
    searchImagesQueryOptions(
      {
        q: `${video.data?.customer?.name} ${selectedCampaign?.["Product-name"] ?? ""}`,
        iterations: 1,
        moderate: true,
        retries: 1,
      },
      {
        enabled:
          !!video.data?.customer?.name && !!selectedCampaign?.["Product-name"],
      },
    ),
  );

  const productSearchQuery = Object.assign(
    {
      label: `[Search] ${selectedCampaign?.["Product-name"]}`,
      url: `https://www.bing.com/images/search?q=${encodeURIComponent(`${video.data?.customer?.name} ${selectedCampaign?.["Product-name"] ?? ""}`)}`,
    },
    _productSearchQuery,
  );

  useEffect(() => {
    if (!video.isSuccess || !videoId || updateVideo.isPending) {
      return;
    }

    const ids = video.data?.selectedCampaignIds ?? [];
    const campaigns = video.data?.campaigns ?? [];
    if (!ids.length) {
      return;
    }

    const invalid = ids.some(
      (id) => !campaigns.some((campaign) => campaign.id === id),
    );
    if (!invalid) {
      return;
    }

    updateVideo.mutate({
      selectedCampaignIds:
        campaigns.length > 0 ? [campaigns[campaigns.length - 1].id] : [],
    });
  }, [
    video.isSuccess,
    videoId,
    video.data?.selectedCampaignIds,
    video.data?.campaigns,
    updateVideo.isPending,
    updateVideo.mutate,
  ]);

  const cropImage = useCropImage();

  const generateVariation = useGenerateVariation();

  const [bannersStatus, setBannersStatus] = useState(null);

  const exportBanners = useExportBanners(videoId, {
    enabled:
      !!template.data?.assetsTemplateId &&
      currentStep === WIZARD_STEP.BANNERS &&
      !!video.data?.templateAssetsRenditions?.length,
  });

  // Retry exporting banners if there were errors -> status="partially_succeeded"
  useEffect(() => {
    if (
      exportBanners.isSuccess &&
      exportBanners.data.some((item) => item.error)
    ) {
      exportBanners.refetch();
    }
  }, [exportBanners.isSuccess, exportBanners.data]);

  const generateBanners = useMutation({
    mutationFn: async ({ videoId }, { client }) => {
      if (!videoId) {
        throw new Error("videoId is required");
      }

      setBannersStatus("Preparing assets");

      const videoData = client.getQueryData(videoKey.detail(videoId));
      const renditionsDocumentId =
        videoData?.templateAssetsRenditions?.[0]?.documentId;
      const assetsTemplateId =
        // renditionsDocumentId ??
        videoData?.assetsTemplateId ?? template.data?.assetsTemplateId;
      const { campaigns, selectedCampaignIds } = videoData;
      const assetsTemplateData = client.getQueryData(
        expressTemplateKey.detail(assetsTemplateId),
      );

      const taggedAssets =
        assetsTemplateData?.pages
          ?.flatMap((apiPage) => apiPage.documentPages)
          ?.flatMap((docPage) =>
            docPage.taggedElements.map((element) => ({
              ...element,
              size: {
                width: Math.round(element.size.width),
                height: Math.round(element.size.height),
              },
            })),
          ) ?? [];

      const newAssets = [];

      const selectedLogo = videoData?.assets?.find(
        (asset) => asset.name === "Logo",
      );

      if (
        !!selectedLogo &&
        !videoData?.assets?.some(
          (asset) =>
            asset.name === "Logo_cropped" && asset.filePath.endsWith(".png"),
        )
      ) {
        const taggedLogos = taggedAssets?.filter(
          (el) => el.type === "image" && el.name.startsWith("Logo #"),
        );

        const biggestTaggedLogo =
          taggedLogos?.reduce((prev, current) => {
            const currentArea = current.size.width * current.size.height;
            const prevArea = prev?.size?.width * prev?.size?.height;
            return currentArea > (prevArea ?? 0) ? current : prev;
          }, null) ?? selectedLogo;

        const minLogoSize = { width: 512, height: 512 };

        if (
          biggestTaggedLogo.size.width < minLogoSize.width ||
          biggestTaggedLogo.size.height < minLogoSize.height
        ) {
          const aspectRatio =
            biggestTaggedLogo.size.width / biggestTaggedLogo.size.height;

          if (biggestTaggedLogo.size.width < minLogoSize.width) {
            biggestTaggedLogo.size.width = minLogoSize.width;
            biggestTaggedLogo.size.height = Math.round(
              minLogoSize.width / aspectRatio,
            );
          } else if (biggestTaggedLogo.size.height < minLogoSize.height) {
            biggestTaggedLogo.size.height = minLogoSize.height;
            biggestTaggedLogo.size.width = Math.round(
              minLogoSize.height * aspectRatio,
            );
          }
        }

        const logoUrl = await client.query(
          presignURLQueryOptions(selectedLogo?.filePath),
        );

        const logoBlob = await getImageBlob({ url: logoUrl.presignURL });
        const pngLogoBlob = await convertImageBlobToType(
          logoBlob.blob,
          "png",
          1,
        );
        const logoRendition = await cropImageBlob(
          pngLogoBlob,
          {
            width: biggestTaggedLogo.size.width,
            height: biggestTaggedLogo.size.height,
          },
          OBJECT_FIT.CONTAIN,
          OBJECT_POSITION.LEFT,
        );

        const croppedLogo = await uploadFile.mutateAsync({
          fileBlob: logoRendition,
          filePath: `${video.data?.projectPath}/Logo_cropped_${biggestTaggedLogo.size.width}x${biggestTaggedLogo.size.height}.png`,
        });

        const croppedLogoAsset = {
          name: "Logo_cropped",
          filePath: croppedLogo.filePath,
          size: biggestTaggedLogo.size,
        };

        newAssets.push(croppedLogoAsset);
      }

      const brandColor =
        videoData?.assets?.find((asset) => asset.name === "Brand-color")
          ?.color ||
        videoData?.customer?.color ||
        "#FFFFFF";

      if (
        !videoData?.assets?.some(
          (asset) => asset.name === "Customer-color-img",
        ) ||
        !videoData?.assets?.find((asset) => asset.name === "Customer-color-img")
          ?.color !== brandColor
      ) {
        const colorImageBlob = await createImageColorBlob(brandColor, 400, 400);

        const customerColorImage = await uploadFile.mutateAsync({
          fileBlob: colorImageBlob,
          filePath: `${video.data?.projectPath}/Customer-color-img.png`,
        });

        const customerColorImageAsset = {
          name: "Customer-color-img",
          filePath: customerColorImage.filePath,
          size: {
            width: 400,
            height: 400,
          },
          color: brandColor,
        };

        newAssets.push(customerColorImageAsset);
      }

      const taggedImages = taggedAssets
        .filter(
          (el) =>
            el.type === "image" &&
            !el.name.startsWith("Logo") &&
            !el.name.startsWith("App-icon") &&
            !videoData?.assets?.some((asset) => asset.name === el.name),
        )
        .map((taggedImage) => {
          const assetName = taggedImage.name.split("#")[0].trim();
          const baseAssetName = assetName.split("-")[0].trim();
          const asset =
            newAssets?.find((a) => a.name === assetName) ??
            newAssets?.find((a) => a.name === baseAssetName) ??
            videoData?.assets?.find((a) => a.name === assetName) ??
            videoData?.assets?.find((a) => a.name === baseAssetName);

          if (!asset?.filePath) {
            console.warn(
              "No filePath found for asset",
              asset,
              "taggedImage:",
              taggedImage,
            );

            return null;
          }

          const mainSubject =
            asset?.labeling?.mainSubject ??
            videoData?.assets?.find(
              (a) => a.name === Object.keys(asset.sourceReferences ?? {})[0],
            )?.labeling?.mainSubject;

          return {
            name: taggedImage.name,
            size: taggedImage.size,
            originalPath: asset?.filePath,
            mainSubject,
            // filePath: asset?.filePath,
            // size: asset?.size,
          };
        })
        .filter(Boolean);

      setBannersStatus("Generating smart crops");

      const cropTargets = new Map();

      await Promise.all(
        taggedImages
          .filter((el) => !el.name.startsWith("Customer-color-img"))
          .map(async (asset) => {
            if (!cropTargets.has(asset.originalPath)) {
              const { presignURL } = await client.query(
                presignURLQueryOptions(asset.originalPath),
              );

              cropTargets.set(asset.originalPath, [
                presignURL,
                new Set([asset.size]),
                new Map([[asset.name, asset]]),
              ]);
            } else {
              cropTargets.get(asset.originalPath)[1].add(asset.size);
              cropTargets.get(asset.originalPath)[2].set(asset.name, asset);
            }
          }),
      );

      const cropTargetEntries = Array.from(cropTargets.values());
      const batchSize = 2; // Adjust based on API limits and performance
      const batches = [];
      for (let i = 0; i < cropTargetEntries.length; i += batchSize) {
        batches.push(cropTargetEntries.slice(i, i + batchSize));
      }

      const smartCropResults = await Promise.allSettled(
        batches.map((batch) =>
          cropImage.mutateAsync({
            items: batch.map(([presignURL, sizes, assetsMap]) => [
              presignURL,
              Array.from(sizes),
              Array.from(assetsMap)?.[0]?.[1]?.mainSubject,
            ]),
          }),
        ),
      );

      const smartCropData = {
        crops: smartCropResults.flatMap((result, idx) =>
          result.status === "fulfilled"
            ? result.value.crops
            : new Array(batches[idx].length).fill({ bboxes: [] }),
        ),
      };

      setBannersStatus("Cropping visuals");

      const assetsToCrop = smartCropData?.crops.flatMap((item, itemIdx) => {
        const [, , assetsMap] = cropTargetEntries[itemIdx];
        const _assets = Array.from(assetsMap.values())
          .map((asset, assetIdx) => {
            const bbox = item.bboxes[assetIdx];

            if (!bbox) {
              console.warn(
                "No bbox found for asset",
                asset,
                "with size",
                asset.size,
              );
              return null;
            }

            return {
              ...asset,
              bbox,
            };
          })
          .filter(Boolean);

        return _assets;
      });

      const croppedPromises = await Promise.all(
        taggedImages.map(async (asset) => {
          try {
            let croppedBlob;

            const smartCropItem = assetsToCrop?.find(
              (a) => a.name === asset.name,
            );

            const s3Asset = await client.query(
              presignURLQueryOptions(asset.originalPath),
            );
            const assetBlob = await getImageBlob({ url: s3Asset.presignURL });

            if (smartCropItem?.bbox) {
              croppedBlob = await cropImageBlobToBbox(
                assetBlob.blob,
                smartCropItem,
              );
            } else {
              croppedBlob = await cropImageBlob(
                assetBlob.blob,
                {
                  width: asset.size.width,
                  height: asset.size.height,
                },
                OBJECT_FIT.COVER,
                OBJECT_POSITION.CENTER,
              );
            }

            const croppedRendition = await uploadFile.mutateAsync({
              fileBlob: croppedBlob,
              filePath: `${video.data?.projectPath}/${asset.name}_cropped_${asset.size.width}x${asset.size.height}.${assetBlob.image.format}`,
            });

            return {
              name: asset.name,
              size: asset.size,
              filePath: croppedRendition.filePath,
            };
          } catch (error) {
            console.warn("Error cropping/uploading asset", asset, error);
            return {
              name: asset.name,
              size: asset.size,
              filePath: asset.originalPath,
            };
          }
        }),
      );

      newAssets.push(...croppedPromises);

      const filteredAssets = newAssets
        .filter(Boolean)
        .filter((asset) => !!asset.filePath);

      const toRemoveAssets = filteredAssets.some(
        (asset) => asset.name === "Customer-color-img",
      )
        ? videoData?.assets?.filter((asset) =>
            asset.name.startsWith("Customer-color-img #"),
          )
        : [];

      if (!!filteredAssets.length) {
        await updateAssets.mutateAsync({
          assets: filteredAssets,
          toRemove: toRemoveAssets,
        });
      }

      const allAssets = client.getQueryData(videoKey.detail(videoId)).assets;

      const selectedCampaign = campaigns.find(
        (campaign) => campaign.id === selectedCampaignIds?.[0],
      );

      let textMappings = {};

      if (videoData?.templateId === "csc-overview") {
        textMappings = {
          "Campaign-tagline-1": selectedCampaign?.taglines?.[0]?.en || "",
          "Campaign-tagline-1-Spanish":
            selectedCampaign?.taglines?.[0]?.es || "",
          "Campaign-tagline-2": selectedCampaign?.taglines?.[1]?.en || "",
          "Campaign-tagline-3": selectedCampaign?.taglines?.[2]?.en || "",
          CTA: "DISCOVER",
          "CTA-Spanish": "DESCUBRIR",
        };
      } else if (template.data?.campaignSelectionType === SELECTION_TYPE.NONE) {
        textMappings =
          videoData?.campaigns?.find(
            (c) => c.id === videoData?.selectedCampaignIds?.[0],
          ) || {};
      }

      // const assetTemplatePagesLength =
      //   assetsTemplateData?.pages
      //     ?.flatMap((apiPage) => apiPage.documentPages)
      //     .pop()?.pageNumber || 1;

      // const MAX_PAGES_PER_REQUEST = 60;
      // const _pages = new Array(
      //   Math.ceil(assetTemplatePagesLength / MAX_PAGES_PER_REQUEST),
      // )
      //   .fill(0)
      //   .map((_, idx) => {
      //     const startPage = idx * MAX_PAGES_PER_REQUEST + 1;
      //     const endPage = Math.min(
      //       startPage + MAX_PAGES_PER_REQUEST - 1,
      //       assetTemplatePagesLength,
      //     );
      //     return `${startPage}-${endPage}`;
      //   });

      setBannersStatus("Creating variations");

      const tagMappingsPromises = await Promise.all(
        assetsTemplate.data?.pages
          ?.flatMap((page) =>
            page.documentPages.flatMap((docPage) => docPage.taggedElements),
          )
          ?.map(async (element) => {
            if (element.type === "text") {
              return {
                [element.name]: textMappings[element.name] || "",
              };
            }

            let assetBaseName = element.name.split("#")[0].trim();

            if (element.name.startsWith("Logo")) {
              assetBaseName = "Logo_cropped";
            } /* else if (element.name.startsWith("App-icon")) {
              assetBaseName = "App-icon-cropped";
            } */

            const matchedAsset =
              allAssets.find((a) => a.name === element.name) ||
              allAssets.find((a) => a.name === assetBaseName);

            if (!matchedAsset) {
              return null;
            }

            const s3Asset = await client.query(
              presignURLQueryOptions(matchedAsset.filePath),
            );

            return {
              [element.name]: s3Asset.presignURL,
            };
          }) ?? [],
      );

      const tagMappings = tagMappingsPromises
        .filter(Boolean)
        .reduce((acc, curr) => ({ ...acc, ...curr }), {});

      if (assetsTemplateId) {
        setBannersStatus("Creating Express document");

        return generateVariation.mutateAsync({
          id: assetsTemplateId,
          batchSize: EXPRESS_TAG_BATCH_SIZE,
          variationDetails: {
            pages: "1-",
            preferredDocumentName: `${videoData?.customer?.name} — ${assetsTemplate?.data?.pages?.[0]?.name || "Assets Template"}`,
            tagMappings,
          },
        });
      }

      return {
        document: { id: null },
      };
    },
    onSuccess: async (data) => {
      const templateAssetsRendition = {
        documentId: data.document.id,
        pages: "1-",
      };

      await updateVideo.mutateAsync({
        templateAssetsRenditions: [templateAssetsRendition],
        templateAssetsRenditionFiles: [],
      });

      // exportBanners.refetch();

      // return exportBanners.mutateAsync(variables);
    },
    onError: (error) => {
      console.error("Error generating banners:", error);
    },
  });

  // Update banners generation status
  useEffect(() => {
    if (exportBanners.isFetching) {
      setBannersStatus(
        exportBanners.data?.length ? "Refreshing banners" : "Exporting banners",
      );
    } else if (exportBanners.isSuccess && !generateBanners.isPending) {
      setBannersStatus(null);
    }
  }, [
    exportBanners.isFetching,
    exportBanners.isSuccess,
    generateBanners.isPending,
  ]);

  // Regenerate banners if assetsTemplate changed
  useEffect(() => {
    if (
      exportBanners.isEnabled &&
      exportBanners.isSuccess &&
      assetsTemplate.isSuccess
    ) {
      const exportedPages = exportBanners.data
        .map((item) => item.pageNumber)
        .sort((a, b) => a - b);
      const templatePages = assetsTemplate.data?.pages
        .flatMap((p) => p.documentPages.map((dp) => dp.pageNumber))
        .sort((a, b) => a - b);

      if (
        // exportedPages.length !== templatePages.length ||
        !templatePages.every((value) => exportedPages.includes(value))
      ) {
        console.log("Regenerating banners due to changes in assets template");
        generateBanners.mutate({ videoId });
      }
    }
  }, [
    videoId,
    exportBanners.isEnabled,
    exportBanners.isSuccess,
    assetsTemplate.isSuccess,
  ]);

  const exportExpressVideo = useExportExpressVideo(videoId, {
    enabled:
      currentStep === WIZARD_STEP.GENERATE_VIDEO &&
      video.data?.templateType === "express",
  });

  const exportVideo = useMutation({
    mutationFn: async (variables, { client, signal }) => {
      const videoData = client.getQueryData(videoKey.detail(variables.videoId));

      if (videoData?.templateType === "express") {
        if (videoData?.videoPath) {
          const presignData = await client.query(
            presignURLQueryOptions(videoData.videoPath),
          );

          if (videoData?.externalSource === "aurora") {
            await putAuroraVideo(videoData.id, { signal });
          }

          return presignData;
        }

        return exportExpressVideo.data;
      } else if (videoData?.templateType === "mogrt") {
        // For MOGRT templates, the video is already rendered and uploaded in S3 in the `renderMogrtTemplate` mutation
        if (videoData?.videoPath) {
          return client.query(presignURLQueryOptions(videoData.videoPath));
        }
        return variables;
      } else if (videoData?.templateType === "modular") {
        // For modular templates, all renders were already completed — no additional export step needed
        return variables;
      } else {
        throw new Error("Unsupported template type");
      }
    },
    onMutate: (variables, { client }) => {
      const videoData = client.getQueryData(videoKey.detail(variables.videoId));

      if (videoData?.templateType === "express" && !videoData?.videoPath) {
        return exportExpressVideo.mutateAsync();
      }
    },
  });

  const generateExpressVideo = useMutation({
    mutationFn: async ({ videoId, selectedCampaign, banners }, { client }) => {
      const videoData = client.getQueryData(videoKey.detail(videoId));
      const { expressTemplateId: templateId } = videoData ?? {};

      const videoTemplateData = client.getQueryData(
        expressTemplateKey.detail(templateId),
      );

      const taggedAssetsPromises = await Promise.allSettled(
        videoTemplateData?.pages
          .flatMap((apiPage) => apiPage.documentPages)
          .flatMap((docPage) => docPage.taggedElements)
          .map((element) =>
            mapTaggedValue("express", element, {
              customerName: videoData?.customer?.name,
              selectedCampaign,
              banners,
              assets: videoData?.assets || [],
              queryClient: client,
              audioVariations: template.data?.audioVariations,
            }),
          ),
      );

      const tagMappings = taggedAssetsPromises
        .filter((promise) => promise.status === "fulfilled" && promise.value)
        .map((promise) => promise.value);

      const sortedTagMappings = tagMappings.sort((a, b) => {
        const keyA = Object.keys(a)[0];
        const keyB = Object.keys(b)[0];
        const indexA = template.data?.taggedElements.indexOf(keyA);
        const indexB = template.data?.taggedElements.indexOf(keyB);

        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;

        return 0;
      });

      const generateData = await generateVariation.mutateAsync({
        id: templateId,
        batchSize: EXPRESS_TAG_BATCH_SIZE,
        variationDetails: {
          pages: "1-",
          preferredDocumentName: `${videoData?.customer?.name} — ${videoTemplateData?.pages?.[0]?.name || ""}`,
          tagMappings: sortedTagMappings.reduce(
            (acc, curr) => ({ ...acc, ...curr }),
            {},
          ),
        },
      });

      trackVideoWizard({
        isConversion: true,
        project: {
          id: videoId,
          name: videoData.name,
          expressId: generateData.document.id,
          videoPath: videoData.videoPath,
        },
        customer: {
          name: video.data?.customer?.name,
          industry: video.data?.customer?.industry,
          drNumber: video.data?.drNumber,
          accountName: video.data?.accountName || accountName || undefined,
        },
        user: { email: userProfile?.email },
      });

      return updateVideo.mutateAsync({
        expressId: generateData.document.id,
      });
    },
    onSuccess: async (data, variables) => {
      return exportVideo.mutateAsync({
        videoId: variables.videoId,
      });
    },
    onError: (error) => {
      console.error("Error generating express video", error);
    },
  });

  useEffect(() => {
    if (
      !video.isSuccess ||
      video.data?.templateType !== "mogrt" ||
      updateVideo.isPending ||
      !template.data
    ) {
      return;
    }

    const resolved = resolveMogrtRootPath(template.data, selectedCampaign);

    if (resolved && resolved !== video.data?.mogrtRootPath) {
      updateVideo.mutate({ mogrtRootPath: resolved });
    }
  }, [
    video.isSuccess,
    video.data?.templateType,
    video.data?.mogrtRootPath,
    template.data,
    selectedCampaign,
    updateVideo.isPending,
  ]);

  const describeMogrtTemplate = useDescribeMogrtTemplate(
    video.data?.mogrtRootPath,
    {
      enabled:
        !!video.data?.mogrtRootPath &&
        video.data?.templateType === "mogrt" &&
        currentStep != null &&
        isStepAtOrAfter(currentStepEntry, WIZARD_STEP.BRANDING),
    },
  );

  const describeModularTemplate = useDescribeModularTemplate(
    video.data?.modules,
    {
      enabled:
        !!video.data?.modules?.length &&
        video.data?.templateType === "modular" &&
        currentStep != null &&
        isStepAtOrAfter(currentStepEntry, WIZARD_STEP.BRANDING),
    },
  );

  const renderMogrtTemplate = useRenderMogrtTemplate({
    onSuccess: (outputUrl) => {
      console.log("render success", outputUrl);
      video.refetch();
    },
  });
  const uploadRenditionFile = useUploadFile();

  // Express rendition URLs expire after ~4h; render from our S3 copies so jobs stay replayable.
  const persistRenditionFiles = async (videoData, renditions) => {
    const files = await Promise.all(
      renditions
        .filter((rendition) => rendition?.renditionUrl)
        .map(async (rendition) => {
          const documentKey = String(
            rendition.id ??
              videoData.templateAssetsRenditions?.[0]?.documentId ??
              "document",
          ).replace(/[^\w-]/g, "_");
          try {
            const { filePath } = await uploadRenditionFile.mutateAsync({
              fileURL: rendition.renditionUrl,
              filePath: `${videoData.projectPath}/renditions/${documentKey}/page-${rendition.pageNumber}.jpg`,
            });
            return {
              pageNumber: rendition.pageNumber,
              documentId: rendition.id ?? null,
              filePath,
            };
          } catch (error) {
            console.warn(
              `Failed to copy rendition page ${rendition.pageNumber} to S3; using the Express URL`,
              error,
            );
            return null;
          }
        }),
    );

    const savedFiles = files.filter(Boolean);
    if (savedFiles.length > 0) {
      await updateVideo.mutateAsync({
        templateAssetsRenditionFiles: savedFiles,
      });
    }
    return savedFiles;
  };

  const generateVideo = useMutation({
    mutationFn: async (
      { videoId, templateAssetsRenditions, resumeMogrtJob },
      { client, signal },
    ) => {
      let videoData = client.getQueryData(videoKey.detail(videoId));

      const assetTemplateData = client.getQueryData(
        expressTemplateKey.detail(videoData?.assetsTemplateId),
      );

      const assetsTemplatePages = assetTemplateData?.pages?.flatMap(
        (apiPage) => apiPage.documentPages,
      );

      const renditionFiles =
        !resumeMogrtJob &&
        videoData?.templateType !== "express" &&
        videoData?.projectPath &&
        templateAssetsRenditions?.length
          ? await persistRenditionFiles(videoData, templateAssetsRenditions)
          : [];
      const renditionFileByPage = new Map(
        renditionFiles.map((file) => [file.pageNumber, file.filePath]),
      );

      const banners =
        templateAssetsRenditions?.map((rendition) => {
          const filePath = renditionFileByPage.get(rendition.pageNumber);
          return {
            ...(filePath ? { filePath } : { url: rendition.renditionUrl }),
            pageNumber: rendition.pageNumber,
            name: assetsTemplatePages
              ?.find((page) => page.pageNumber === rendition.pageNumber)
              ?.pageTitle?.split(" - ")[0],
          };
        }) ?? [];

      const selectedCampaign = videoData?.campaigns?.find(
        (campaign) => campaign.id === videoData?.selectedCampaignIds?.[0],
      );

      if (videoData?.templateType === "express") {
        return generateExpressVideo.mutateAsync({
          videoId,
          selectedCampaign,
          banners,
        });
      } else if (videoData?.templateType === "mogrt") {
        if (resumeMogrtJob && videoData?.jobId && !videoData?.videoPath) {
          return renderMogrtTemplate.mutateAsync(
            {
              videoId,
              existingJobId: String(videoData.jobId),
            },
            { signal },
          );
        }

        if (!describeMogrtTemplate.isSuccess) {
          await new Promise((resolve) => {
            const interval = setInterval(() => {
              if (describeMogrtTemplate.isSuccess) {
                clearInterval(interval);
                resolve();
              }
            }, 100);
          });
        }

        const formValues = await buildMogrtFormValues(
          describeMogrtTemplate.data.controls,
          {
            customerName: videoData?.customer?.name,
            selectedCampaign: {
              "Persona Selection": personaId,
              ...selectedCampaign,
            },
            banners,
            assets: videoData?.assets || [],
            queryClient: client,
            audioVariations: template.data?.audioVariations,
            customer: {
              "Brand-color":
                videoData?.customer?.["Brand-color"] ??
                videoData?.customer?.color,
              "Secondary-color":
                videoData?.customer?.["Secondary-color"] ??
                videoData?.customer?.secondaryColor,
            },
            musicPath: videoData?.musicPath,
          },
        );

        console.log("Form values for MOGRT template:", formValues);

        return renderMogrtTemplate.mutateAsync(
          {
            ...describeMogrtTemplate.data,
            formValues,
            videoId,
            audioVariations: template.data?.audioVariations,
          },
          { signal },
        );
      } else if (videoData?.templateType === "modular") {
        const scenes = getVideoScenes(videoData);
        const resolvedModules = videoData.modules?.length
          ? videoData.modules
          : buildModulesFromScenes(scenes, template.data?.modules);

        const modularData = await client.query(
          modularTemplateQueryOptions(resolvedModules),
        );

        const identity = await getAlloy()?.("getIdentity", {
          namespaces: ["ECID"],
        });
        const ecid = identity?.identity?.ECID;

        const formValuesContext = {
          customerName: videoData?.customer?.name,
          selectedCampaign: {
            "Persona Selection": personaId,
            ...selectedCampaign,
          },
          banners,
          assets: videoData?.assets || [],
          queryClient: client,
          audioVariations: template.data?.audioVariations,
          customer: {
            "Brand-color":
              videoData?.customer?.["Brand-color"] ??
              videoData?.customer?.color,
            "Secondary-color":
              videoData?.customer?.["Secondary-color"] ??
              videoData?.customer?.secondaryColor,
          },
          musicPath: videoData?.musicPath,
          templateVideoAssets: collectTemplateVideoAssets(
            template.data,
            videoData,
          ),
        };

        const sceneAssignments = assignScenesToModules(scenes);
        const modulePayloads = await Promise.all(
          modularData.modules.map(async (moduleData, index) => {
            const baseFormValues = await buildMogrtFormValues(
              moduleData.controls,
              formValuesContext,
            );

            const sceneFormValues = await applyScenesToModuleControls(
              sceneAssignments[index]?.scenes ?? [],
              moduleData.controls,
              formValuesContext,
            );

            const formValues = { ...baseFormValues, ...sceneFormValues };

            console.log("Form values for modular module:", {
              index,
              name: moduleData.name,
              scenes: sceneAssignments[index]?.scenes,
              formValues,
            });

            const payload = buildRenderPayload({
              ...moduleData,
              formValues,
              audioVariations: template.data?.audioVariations,
            });
            return { ...payload, moduleName: moduleData.name };
          }),
        );

        const renderResponse = await renderModularTemplateService(
          {
            videoId,
            ecid,
            modules: modulePayloads,
          },
          { signal },
        );

        const modularJobs = renderResponse?.modularJobs;
        if (!Array.isArray(modularJobs) || modularJobs.length === 0) {
          throw new Error("Failed to start modular render. Please try again.", {
            cause: renderResponse,
          });
        }

        await updateVideo.mutateAsync({
          status: "processing",
          modularJobs,
          modularRenderJobId: renderResponse?.jobId,
        });

        return modularJobs;
      } else {
        throw new Error("Unsupported template type");
      }
    },
    onMutate: async ({ videoId }, { client }) => {
      const videoData = client.getQueryData(videoKey.detail(videoId));

      const previous = {
        jobId: videoData?.jobId ?? null,
        videoPath: videoData?.videoPath ?? null,
      };

      if (videoData?.videoPath) {
        await updateVideo.mutateAsync({
          videoPath: null,
          status: "processing",
        });
      }

      return previous;
    },
    onSuccess: async (data, variables, onMutateResult, { client }) => {
      const { videoId } = variables;

      await client.refetchQueries({ queryKey: videoKey.detail(videoId) });
      const videoData = client.getQueryData(videoKey.detail(videoId));

      return exportVideo.mutateAsync({
        videoId,
        videoPath: videoData?.videoPath,
      });
    },
    onError: (error, variables, onMutateResult) => {
      console.error("Error generating video", error);

      if (isCancelledError(error) || error?.cause?.problem === "CANCEL_ERROR") {
        return;
      }

      const previous = onMutateResult;
      if (previous && (previous.jobId != null || previous.videoPath != null)) {
        updateVideo.mutate({
          status: previous.videoPath != null ? "completed" : "processing",
          jobId: previous.jobId,
          videoPath: previous.videoPath,
        });
      }
    },
  });

  // Create the video for this wizard run. Self-guarding so it can be called
  // unconditionally as part of the CAMPAIGN step's entering action (see
  // `DEFAULT_STEP_ACTIONS` in wizardSteps.js) instead of firing from a
  // `useEffect`. Accepts an optional `overrideId` for the case where the caller
  // has just generated the project id in the same tick (React state for it
  // hasn't propagated to this hook's `videoId` yet).
  const createVideoIfNeeded = useCallback(
    async (overrideId, onSuccess) => {
      if (!!video.data?.id) {
        onSuccess?.(video.data, { videoId: video.data.id });
        return;
      }

      const id = overrideId ?? videoId;
      // Resolve the ldap from the live store instead of the closure-captured
      // `userProfile`, which would be stale for the duration of this callback.
      const ldap = await getUserLdap();
      const projectPath = `${ldap}/videos/${id}`;

      const data = {
        id,
        templateId: template.data?.id,
        templateType: template.data?.type,
        assetsTemplateId: template.data?.assetsTemplateId,
        name: `${customerName} — ${template.data?.name ?? "BeStory Video"}`,
        projectPath,
        accountName,
        drNumber,
        customer: {
          name: customerName,
        },
        hint,
        product,
        assets: [],
      };

      if (template.data?.id === "firefly-enterprise") {
        data.include3DDigitalTwin = include3DDigitalTwin;
      }

      if (template.data?.type === "express") {
        data.expressTemplateId = template.data?.expressId;
      } else if (
        template.data?.type === "mogrt" ||
        template.data?.type === "modular"
      ) {
        data.mogrtRootPath = template.data?.rootPath;
      }

      if (!!template.data?.personas?.length) {
        data.personaId = personaId;
      }

      if (!!template.data?.verticals?.length) {
        console.log("Selected vertical key:", verticalKey, selectedVertical);
        data.verticalKey =
          verticalKey ??
          template.data?.verticals?.find((vertical) => vertical.isDefault)
            ?.key ??
          "fashion";

        const verticalAssets = template.data?.verticals?.find(
          (vertical) => vertical.key === data.verticalKey,
        )?.assets;

        if (verticalAssets) {
          data.assets.push(...verticalAssets);
        }
      }

      createVideo.mutate(data, {
        onSuccess: (data, variables) => {
          setIsVideoCreated(true);
          onCreateProject?.(data, variables);

          if (onSuccess) {
            return onSuccess(data, variables);
          }
        },
      });
    },
    [
      createVideo.isIdle,
      createVideo.mutate,
      video.data?.id,
      videoId,
      personaId,
      verticalKey,
      selectedVertical,
      template.data,
      customerName,
      hint,
      product,
      include3DDigitalTwin,
      accountName,
      drNumber,
      onCreateProject,
    ],
  );

  // Setup current step
  useEffect(() => {
    if (
      currentStep === null &&
      (video.isSuccess || !video.isEnabled) &&
      (template.isSuccess || !template.isEnabled) &&
      !createVideo.isSuccess
    ) {
      // Prefer the step from the URL: render it if ready, otherwise walk back to
      // the first ready step (a step's `enter.isReady`, or `true` when it has no
      // entering action). Unknown/disabled steps fall through to the data-driven
      // resolution below.
      if (routeStep) {
        const resolvedTemplateId = templateId ?? template.data?.id;
        const steps = resolveSteps(resolvedTemplateId, template.data);
        const startIndex = steps.findIndex((step) => step.id === routeStep);

        if (startIndex !== -1) {
          const readinessCtx = {
            video,
            template,
            assetsTemplate,
            selectedCampaign: video.data?.campaigns?.find(
              (campaign) =>
                campaign.id === video.data?.selectedCampaignIds?.[0],
            ),
          };
          const isStepReady = (step) => {
            // An images step is where its variations are generated, so being
            // "not ready" is its normal state — it stays reachable, and the
            // step arms the generation itself.
            if (isImagesStep(step)) {
              return true;
            }

            const action = getStepEnterAction(step, resolvedTemplateId);
            return action?.isReady ? action.isReady(readinessCtx) : true;
          };

          for (let index = startIndex; index >= 0; index -= 1) {
            if (isStepReady(steps[index])) {
              setCurrentStep(steps[index].id);
              return;
            }
          }

          setCurrentStep(steps[0].id);
          return;
        }
      }

      if (
        video.data?.videoPath &&
        (video.data?.templateType === "express"
          ? !!video.data?.expressId
          : true)
      ) {
        // onSuccess?.(video.data);
        exportVideo.mutate({
          videoId: video.data.id,
          videoPath: video.data.videoPath,
        });
        setCurrentStep(WIZARD_STEP.GENERATE_VIDEO);
      } else if (video.data?.jobId || video.data?.modularJobs?.length) {
        setCurrentStep(WIZARD_STEP.GENERATE_VIDEO);
      } else if (
        video.data?.templateAssetsRenditions?.length &&
        hasBannersStep(template.data)
      ) {
        setCurrentStep(WIZARD_STEP.BANNERS);
      } else if (
        hasImageVariationsStep(template.data) &&
        template.data?.imageVariations?.some((variation) =>
          video.data?.assets?.some((a) => a.name === variation.key),
        )
      ) {
        // Resume at the first stage that still has variations to generate.
        setCurrentStep(
          getResumeImagesStepId(
            templateId ?? template.data?.id,
            template.data,
            video.data,
          ) ?? WIZARD_STEP.IMAGES,
        );
      } else if (video.data?.selectedCampaignIds?.length) {
        if (
          hasAudioStep(template.data) &&
          !template.data?.assets
            ?.filter((asset) => asset.type === "audio")
            ?.every((asset) =>
              video.data?.assets?.find((a) => a.name === asset.name),
            )
        ) {
          setCurrentStep(WIZARD_STEP.VOICEOVER);
        } else {
          setCurrentStep(WIZARD_STEP.BRANDING);
        }
      } else if (video.data?.customer?.name) {
        setCurrentStep(WIZARD_STEP.CAMPAIGN);
      } else if (video.data?.templateId) {
        setCurrentStep(WIZARD_STEP.CUSTOMER_DETAILS);
      } else if (templateId) {
        // A template was preselected before any video exists (e.g. picked on the
        // home page). Skip template selection — jump straight to customer details
        // when account details were already provided, otherwise collect them.
        const hasAccountDetails =
          sfType === "none" || !!accountName || !!drNumber;
        setCurrentStep(
          hasAccountDetails
            ? WIZARD_STEP.CUSTOMER_DETAILS
            : WIZARD_STEP.ACCOUNT_DETAILS,
        );
      } else {
        setCurrentStep(WIZARD_STEP.SELECT_TEMPLATE);
      }
    }
  }, [
    video.isSuccess,
    templates.isSuccess,
    template.isSuccess,
    createVideo.isSuccess,
    routeStep,
  ]);

  // Track video wizard events
  useEffect(() => {
    trackVideoWizard({
      isConversion: false,
      project: {
        id: video.data?.id,
        name: video.data?.name,
        expressId: video.data?.expressId,
        videoPath: video.data?.videoPath,
      },
      step: getStepName(
        template.data?.id ?? templateId,
        currentStep,
        template.data,
      ),
      customer: {
        name: video.data?.customer?.name,
        industry: video.data?.customer?.industry,
        drNumber: video.data?.drNumber,
        accountName: video.data?.accountName || accountName || undefined,
      },
      user: { email: userProfile?.email },
    });
  }, [currentStep]);

  const handleCustomerNameChange = useCallback(
    (name) => {
      updateVideo.mutate({
        name: `${name} — ${expressTemplate.data?.pages?.[0]?.name ?? "BeStory Project"}`,

        // Trigger campaign regeneration
        customer: {
          name: name,
        },
        assets: [],
        campaigns: [],
        selectedCampaignIds: [],
      });
    },
    [updateVideo.mutate, expressTemplate.data?.pages],
  );
  const debouncedHandleCustomerNameChange = useDebounce(
    handleCustomerNameChange,
    1000,
  );

  // Update customer name
  useEffect(() => {
    if (
      video.isEnabled &&
      video.isSuccess &&
      customerName &&
      video.data?.customer?.name !== customerName
    ) {
      debouncedHandleCustomerNameChange(customerName);
    }
  }, [
    video.isEnabled,
    video.isSuccess,
    customerName,
    updateCustomer.mutate,
    expressTemplate.data?.pages,
  ]);

  // Update persona
  // useEffect(() => {
  //   if (
  //     video.isSuccess &&
  //     video.data?.templateId === "ucx" &&
  //     typeof personaId !== "undefined" &&
  //     video.data?.personaId !== personaId
  //   ) {
  //     updateVideo.mutate({
  //       personaId,

  //       // Trigger campaign regeneration
  //       customer: {
  //         name: video.data?.customer?.name,
  //       },
  //       assets: [],
  //       campaigns: [],
  //       selectedCampaignIds: [],
  //     });
  //   }
  // }, [
  //   video.isSuccess,
  //   video.data,
  //   personaId,
  //   customerBranding.isEnabled,
  //   updateVideo.mutate,
  // ]);

  // Set brand color asset if missing
  // useEffect(() => {
  //   if (
  //     video.isEnabled &&
  //     video.isSuccess &&
  //     !video.data?.assets?.some((a) => a.name === "Brand-color")
  //   ) {
  //     const color =
  //       video.data?.customer?.color ||
  //       customerBranding.data?.pages?.[0]?.customer?.color;

  //     if (color) {
  //       updateAssets.mutate({
  //         assets: [
  //           {
  //             name: "Brand-color",
  //             type: "color",
  //             color: color,
  //           },
  //         ],
  //       });
  //     }
  //   }
  // }, [
  //   video.isEnabled,
  //   video.isSuccess,
  //   video.data?.assets,
  //   customerBranding.data,
  //   updateAssets.mutate,
  // ]);

  const scrapedAssets = scrapeQueries.flatMap((query) => query.data || []);

  // Update logo asset
  useEffect(() => {
    if (
      template.isSuccess &&
      !!template.data?.assets?.some((asset) => asset.name === "Logo") &&
      video.isSuccess &&
      !video.data?.assets?.some((asset) => asset.name === "Logo") &&
      !uploadAssets.isPending
    ) {
      const scrapedLogo =
        scrapedAssets.find((asset) => asset?.isLogo) ??
        scrapedAssets.find((asset) => {
          const { alt, src } = asset || {};
          const fileName = (
            src?.split("/").pop().toLowerCase() || ""
          ).replaceAll(/[\W_]+/g, " ");
          const _customerName = video.data?.customer?.name
            ?.toLowerCase()
            .replaceAll(/[\W_]+/g, " ");

          return [alt, fileName].some((str) =>
            [/* _customerName,  */ "logo"].some((term) => str?.includes(term)),
          );
        });

      if (scrapedLogo) {
        uploadAssets.mutate({
          assets: [
            {
              name: "Logo",
              type: "image",
              src: scrapedLogo.src,
              size: {
                width: scrapedLogo.width,
                height: scrapedLogo.height,
              },
            },
          ],
        });
      }
    }
  }, [
    template.isSuccess,
    template.data?.assets,
    video.isSuccess,
    video.data?.assets,
    video.data?.customer?.name,
    scrapedAssets,
    uploadAssets.isMutating,
    uploadAssets.mutate,
  ]);

  const [generateStatusText, setGenerateStatusText] =
    useState(GENERATE_STATUS_TEXT);

  // Set additional status text for Express videos
  useEffect(() => {
    if (video.data?.templateType === "express") {
      setGenerateStatusText((prev) => [
        ...prev,
        "Preparing editable Express file",
      ]);
    } else if (generateStatusText.includes("Preparing editable Express file")) {
      setGenerateStatusText((prev) =>
        prev.filter((text) => text !== "Preparing editable Express file"),
      );
    }
  }, [video.data?.templateType]);

  return {
    currentStep,
    setCurrentStep,
    video,
    createVideo,
    createVideoIfNeeded,
    updateVideo,
    updateCustomer,
    updateAssets,
    uploadAssets,
    templates,
    template: {
      ...template,
      hasAudioStep,
    },
    assetsTemplate,
    drNumbers,
    accounts,
    personas,
    selectedPersona,
    setSelectedPersona,
    verticals,
    selectedVertical,
    setSelectedVertical,
    include3DDigitalTwin,
    setInclude3DDigitalTwin,
    customerBranding,
    addCampaign,
    editCampaign,
    scrapeQueries,
    searchImagesQueries,
    appStoreQuery,
    productSearchQuery,
    // taglines,
    bannersStatus,
    generateBanners,
    exportBanners,
    generateVideo,
    generateStatusText,
    renderProgress: renderMogrtTemplate.progress,
    exportVideo,
    describeMogrtTemplate,
    describeModularTemplate,
  };
};

async function buildMogrtFormValues(controls, context) {
  const promises = controls
    .map((control) => {
      if (
        !["text", "media", "dropdown", "audio", "slider"].includes(control.type)
      ) {
        return null;
      }
      const [, mediaLabel] = /^GREEN-(.*)\.\w{3,4}$/i.exec(control.label) ?? [];
      let name,
        type = control.type;
      switch (control.type) {
        case "text":
        case "dropdown":
        case "slider":
        case "audio":
          name = control.label;
          break;
        case "media":
          name = mediaLabel ?? control.label;
          type = "image";
          break;
        default:
          return null;
      }
      return { variableId: control.variableId, name, type };
    })
    .map(async (tag) => {
      if (!tag) return null;
      const value = await mapTaggedValue("mogrt", tag, context);
      if (value == null) return null;
      return { [tag.variableId]: value };
    });

  return (await Promise.allSettled(promises))
    .map((r) => (r.status === "fulfilled" && r.value ? r.value : null))
    .filter(Boolean)
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});
}

async function mapTaggedValue(
  type,
  element,
  {
    customerName,
    selectedCampaign,
    banners,
    assets,
    queryClient,
    audioVariations,
    customer,
    musicPath,
  },
) {
  let assetBaseName = element.name.split("#")[0].trim();

  if (assets.some((a) => a.name === element.name && !!a?.isSkipped)) {
    return null;
  }

  if (element.type === "text") {
    let value = selectedCampaign?.[assetBaseName];

    if (!value) {
      switch (assetBaseName) {
        case "Customer-name":
          value = customerName;
          break;
        case "Campaign-name-1":
          // value =
          //   video.data?.audiencesVariations?.[0]?.campaignTitle || "";
          value =
            selectedCampaign?.taglines?.[0]?.name ??
            `${customerName} Boost campaign`;
          break;
        case "Campaign-name-2":
          // value =
          //   video.data?.audiencesVariations?.[1]?.campaignTitle || "";
          value =
            selectedCampaign?.taglines?.[1]?.name ??
            `${customerName} Winter campaign`;
          break;
        case "Campaign-name-3":
          // value =
          //   video.data?.audiencesVariations?.[2]?.campaignTitle || "";
          value =
            selectedCampaign?.taglines?.[2]?.name ??
            `${customerName} Social campaign`;
          break;
        case "Campaign-tagline-1":
          value = selectedCampaign?.taglines?.[0]?.en || "";
          break;
        case "Campaign-tagline-1-Spanish":
          value = selectedCampaign?.taglines?.[0]?.es || "";
          break;
        case "Campaign-tagline-2":
          value = selectedCampaign?.taglines?.[1]?.en || "";
          break;
        case "Campaign-tagline-3":
          value = selectedCampaign?.taglines?.[2]?.en || "";
          break;
        case "CTA":
          value = "DISCOVER";
          break;
        case "CTA-Spanish":
          value = "DESCUBRIR";
          break;
        case "CTA-final":
          // value = "DESCUBRIR";
          break;
      }
    }

    // Brand/secondary colors live on the customer (as hex). Fill a text control
    // like "Brand-color" with that value, stripping a leading "#" to match the
    // mogrt default's no-"#" format (e.g. "584CCC").
    if (!value && customer?.[assetBaseName]) {
      value = String(customer[assetBaseName]).replace(/^#/, "");
    }

    // TODO: handle text tags
    return !!value
      ? type === "express"
        ? {
            [element.name]: value,
          }
        : value
      : null;
  }

  if (element.type === "dropdown") {
    return selectedCampaign?.[assetBaseName] ?? 0;
  }

  if (element.type === "slider") {
    let altName = assetBaseName;
    let shouldConvertToRGB = false;

    if (element.name.startsWith("Brand-color")) {
      altName = "Brand-color";
      shouldConvertToRGB = true;
    }

    if (element.name.startsWith("Secondary-color")) {
      altName = "Secondary-color";
      shouldConvertToRGB = true;
    }

    let raw =
      assets?.find((a) => a.name === altName)?.color ??
      customer?.[altName] ??
      selectedCampaign?.[altName];
    if (raw == null || raw === "") {
      return null;
    }

    if (shouldConvertToRGB) {
      const rgb = parseColor(raw).toFormat("rgb");

      if (element.name.includes("--R")) {
        raw = rgb.getChannelValue("red");
      } else if (element.name.includes("--G")) {
        raw = rgb.getChannelValue("green");
      } else if (element.name.includes("--B")) {
        raw = rgb.getChannelValue("blue");
      }
    }

    let num;
    if (typeof raw === "number" && Number.isFinite(raw)) {
      num = Math.round(raw);
    } else {
      const s = String(raw).trim().replace(/,/g, "");
      const kMatch = s.match(/^([\d.]+)\s*k\s*$/i);
      const parsed = kMatch ? parseFloat(kMatch[1]) * 1000 : parseFloat(s);
      if (!Number.isFinite(parsed)) {
        return null;
      }
      num = Math.round(parsed);
    }

    return type === "express" ? { [element.name]: num } : num;
  }

  if (element.type === "image") {
    let altName;
    const isLogo =
      element.name.startsWith("Customer-logo") ||
      element.name.startsWith("Company-logo") ||
      element.name.toLowerCase().startsWith("logo");
    if (isLogo) {
      assetBaseName = "Logo_cropped";
    } else if (element.name.startsWith("App-icon")) {
      altName = "App-icon-cropped";
    }

    const elementName = element.name.replace(/\..*$/, "").trim();

    // A banner asset name may carry a trailing dot the control label does not
    // (or vice versa); ignore it when matching.
    const stripTrailingDot = (name) => (name ?? "").replace(/\.$/, "");

    const matchedAsset =
      (!!altName
        ? banners?.find((banner) => banner.name === altName)
        : undefined) ??
      banners?.find(
        (banner) =>
          stripTrailingDot(banner.name) === stripTrailingDot(assetBaseName),
      ) ??
      assets?.find((a) => a.name === elementName) ??
      assets?.find((a) => a.name === assetBaseName) ??
      (isLogo
        ? assets?.find((a) => a.name?.toLowerCase() === "logo")
        : undefined);

    if (!matchedAsset) {
      return null;
    }

    if (matchedAsset.url) {
      if (type === "express") {
        return {
          [element.name]: matchedAsset.url,
        };
      }

      return {
        url: matchedAsset.url,
      };
    }

    const s3Asset = await queryClient.query(
      presignURLQueryOptions(matchedAsset.filePath),
    );

    return type === "express"
      ? {
          [element.name]: s3Asset.presignURL,
        }
      : { url: s3Asset.presignURL };
  }

  if (element.type === "audio") {
    const variation =
      audioVariations?.find((v) => v.label === element.name) ??
      audioVariations?.[0];

    let matchedAsset;
    if (variation) {
      matchedAsset = assets?.find(
        (a) => a.type === "audio" && a.name === variation.key,
      );
    }

    if (!matchedAsset) {
      if (musicPath) {
        const s3Music = await queryClient.query(
          presignURLQueryOptions(musicPath),
        );
        return type === "express"
          ? { [element.name]: s3Music.presignURL }
          : { url: s3Music.presignURL };
      }
      return null;
    }

    if (matchedAsset.url) {
      return {
        url: matchedAsset.url,
      };
    }

    const s3Asset = await queryClient.query(
      presignURLQueryOptions(matchedAsset.filePath),
    );

    return type === "express"
      ? {
          [element.name]: s3Asset.presignURL,
        }
      : { url: s3Asset.presignURL };
  }
}
